/**
 * PostHogTracker — à placer dans le Router.
 * Seul endroit qui décide de ce que PostHog a le droit d'enregistrer.
 *
 * Session replay : essai en cours UNIQUEMENT — aucun autre compte n'en consomme.
 * Events seuls, sans replay : visiteurs non connectés (funnel d'acquisition) et offre
 * Basic gratuite (funnel d'upsell Basic → Premium).
 * Rien du tout : abonnés payants, essais expirés, comptes sans essai, comptes internes.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  applyPostHogTrackingMode,
  capturePageView,
  identifyUser,
  isBasicSubscriptionType,
  isInternalPostHogEmail,
  registerSuperProperties,
  resetUser,
  type PostHogTrackingMode,
} from '../lib/posthog';

/** trial_day 0..7 (bornes essai config.trial_days) / trial_stage early|mid|late — tracking.tsv P1. */
function getTrialDayStage(createdAt?: Date | null): { trial_day: number; trial_stage: 'early' | 'mid' | 'late' } {
  const daysSince = createdAt ? Math.floor((Date.now() - createdAt.getTime()) / 86400000) : 0;
  const trial_day = Math.min(Math.max(daysSince, 0), 7);
  const trial_stage = trial_day <= 2 ? 'early' : trial_day <= 5 ? 'mid' : 'late';
  return { trial_day, trial_stage };
}

type TrackingMode = 'visitor' | 'trial' | 'basic' | 'off';

/** Un seul mode ouvre le replay : `trial`. */
const POSTHOG_MODE: Record<TrackingMode, PostHogTrackingMode> = {
  visitor: 'events',
  trial: 'trial',
  basic: 'events',
  off: 'off',
};

export function PostHogTracker() {
  const location = useLocation();
  const {
    user,
    loading,
    isPremium,
    isTrial,
    isHomePromo,
    hasActiveTrialOnboarding,
    subscriptionType,
    userCreatedAt,
  } = useAuth();
  const identifiedUid = useRef<string | null>(null);
  const trackingModeRef = useRef<TrackingMode | null>(null);
  const lastPageViewRef = useRef<string | null>(null);

  const isInternalUser = !!user?.email && isInternalPostHogEmail(user.email);
  const isBasicPlan = isBasicSubscriptionType(subscriptionType);

  // `null` tant que le profil Firestore charge : on n'applique aucun mode, et posthog-js
  // est opt-out par défaut (initPostHog) — donc rien ne part avant cette décision.
  //
  // Essai en cours = hasActiveTrialOnboarding (AuthContext), seule définition du projet :
  // (isTrial || isHomePromo) && !isTrialExpired && !hasPaidSubscription. Tester isTrial ||
  // isHomePromo directement laissait tous les anciens homePromo devenus payants en replay
  // à vie — le flag homePromo est un marqueur d'acquisition, jamais nettoyé au paiement.
  // isBasicPlan prime : le cron de fin d'essai bascule subscriptionType sur 'Mensuel Basic'
  // mais laisse isTrial à true (tracking.tsv A014).
  const mode: TrackingMode | null = useMemo(() => {
    if (loading) return null;
    if (isInternalUser) return 'off';
    if (!user) return 'visitor';
    if (isBasicPlan) return 'basic';
    return hasActiveTrialOnboarding ? 'trial' : 'off';
  }, [loading, isInternalUser, user, isBasicPlan, hasActiveTrialOnboarding]);

  useEffect(() => {
    if (!mode || mode === 'off') return;

    const pageKey = location.pathname + location.search;
    if (lastPageViewRef.current === pageKey) return;

    lastPageViewRef.current = pageKey;
    capturePageView(pageKey);
  }, [location.pathname, location.search, mode]);

  useEffect(() => {
    if (!mode) return;

    if ((mode === 'trial' || mode === 'basic') && user) {
      const identityKey = [
        user.uid,
        user.email ?? '',
        user.displayName ?? '',
        isPremium,
        isTrial,
        isHomePromo,
        subscriptionType,
        userCreatedAt?.toISOString() ?? '',
      ].join('|');

      if (trackingModeRef.current === mode && identifiedUid.current === identityKey) {
        return;
      }

      if (trackingModeRef.current !== mode) {
        applyPostHogTrackingMode(POSTHOG_MODE[mode]);
      }

      trackingModeRef.current = mode;
      identifiedUid.current = identityKey;
      identifyUser({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        isPremium,
        isTrial,
        isHomePromo,
        isBasic: isBasicPlan,
        subscriptionType,
        createdAt: userCreatedAt,
      });
      registerSuperProperties(
        mode === 'trial' ? getTrialDayStage(userCreatedAt) : { trial_day: 7, trial_stage: 'ended' },
      );
      return;
    }

    if (mode === 'visitor') {
      if (trackingModeRef.current === 'visitor') return;

      trackingModeRef.current = 'visitor';
      applyPostHogTrackingMode(POSTHOG_MODE.visitor);
      if (identifiedUid.current) {
        identifiedUid.current = null;
        resetUser();
      }
      return;
    }

    if (trackingModeRef.current === 'off') return;

    trackingModeRef.current = 'off';
    applyPostHogTrackingMode(POSTHOG_MODE.off);
    if (identifiedUid.current) {
      identifiedUid.current = null;
      resetUser();
    }
  }, [mode, user, isPremium, isTrial, isHomePromo, isBasicPlan, subscriptionType, userCreatedAt]);

  return null;
}
